import { GATEWAY_API_URL } from '@admin-panel/config/env';
import {
  IRiderCashHandover,
  IRiderCashHandoverCandidate,
  RiderCashHandoverProvider,
  RiderCashHandoverStatus,
} from '@admin-panel/types';
import CrudApiClient from './crudapiclient';
import { axiosInstance } from './axios';

type SearchFilter = {
  filterType: 'EQ';
  field: string;
  value: string;
};

type SearchQuery = {
  filters: Array<SearchFilter>;
};

type LyskraftEnvelope<T> = {
  data?: T;
  success?: boolean;
  status?: {
    code?: number;
    key?: string;
    type?: string;
    message?: string;
  };
};

export type RiderCashHandoverSearchRequest = {
  riderId?: number;
  facilityId?: number;
  sourceFacilityId?: number;
  receivingFacilityId?: number;
  packageId?: number;
  collectionPartyType?: RiderCashHandoverProvider;
  salesChannelOrderId?: string;
  customerName?: string;
  statuses?: Array<RiderCashHandoverStatus>;
  deliveredFrom?: string;
  deliveredTo?: string;
  closedFrom?: string;
  closedTo?: string;
};

export type RiderCashHandoverCreatePayload = {
  riderId: number;
  facilityId?: number;
  packageIds: Array<number>;
  remarks?: string;
};

export type RiderCashHandoverDirectCollectPayload = {
  packageId: number;
  hubCountedAmount: number;
  receivingFacilityId?: number;
  remarks?: string;
  disputeReason?: string;
};

export type RiderCashHandoverRiderConfirmRequest = {
  riderConfirmedAmount?: number;
  remarks?: string;
};

export type RiderCashHandoverRiderInitiateRequest = {
  packageId: number;
  riderConfirmedAmount?: number;
  remarks?: string;
};

export type RiderCashHandoverHubConfirmRequest = {
  otp: string;
  hubCountedAmount?: number;
  receivingFacilityId?: number;
  remarks?: string;
  disputeReason?: string;
};

export type RiderCashHandoverCancelRequest = {
  reason: string;
};

export type RiderCashHandoverMoveToDisputeRequest = {
  actualCollectedAmount: number;
  receivingFacilityId?: number;
  disputeReason: string;
  remarks?: string;
};

export type RiderCashHandoverDisputeCloseRequest = {
  actualCollectedAmount: number;
  physicalReceivedAmount?: number;
  receivingFacilityId?: number;
  remarks: string;
};

class RiderCashHandoverApiClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/order/rider-cash-handover');
  }

  async searchCandidates(
    query: RiderCashHandoverSearchRequest,
    limit = 100,
  ): Promise<Array<IRiderCashHandoverCandidate>> {
    const response = await axiosInstance.post(`${this.baseURL}/candidates/search?limit=${limit}`, query);
    return this.transformList(unwrapEnvelope<Array<IRiderCashHandoverCandidate>>(response.data, 'Could not load cash handover candidates') ?? []);
  }

  async searchHandovers(
    query: RiderCashHandoverSearchRequest,
    page = 1,
    size = 20,
  ): Promise<Array<IRiderCashHandover>> {
    const response = await axiosInstance.post(`${this.baseURL}/search?page=${page}&size=${size}`, toSearchQuery(query));
    return this.transformList(unwrapEnvelope<Array<IRiderCashHandover>>(response.data, 'Could not load cash handovers') ?? [])
      .map(stripOtpFromHandover);
  }

  async createHandover(payload: RiderCashHandoverCreatePayload): Promise<IRiderCashHandover> {
    const response = await axiosInstance.post(`${this.baseURL}/`, payload);
    return this.transformNode(unwrapEnvelope<IRiderCashHandover>(response.data, 'Could not create cash handover'));
  }

  async directCollect(payload: RiderCashHandoverDirectCollectPayload): Promise<IRiderCashHandover> {
    const response = await axiosInstance.post(`${this.baseURL}/direct-collect`, payload);
    return this.transformNode(unwrapEnvelope<IRiderCashHandover>(response.data, 'Could not collect courier cash'));
  }

  async riderConfirm(
    id: number,
    payload: RiderCashHandoverRiderConfirmRequest,
  ): Promise<IRiderCashHandover> {
    const response = await axiosInstance.put(`${this.baseURL}/${id}/rider-confirm`, payload ?? {});
    return this.transformNode(unwrapEnvelope<IRiderCashHandover>(response.data, 'Could not confirm cash handover'));
  }

  async riderInitiate(payload: RiderCashHandoverRiderInitiateRequest): Promise<IRiderCashHandover> {
    const response = await axiosInstance.post(`${this.baseURL}/rider-initiate`, payload);
    return this.transformNode(unwrapEnvelope<IRiderCashHandover>(response.data, 'Could not generate cash handover OTP'));
  }

  async hubConfirm(
    id: number,
    payload: RiderCashHandoverHubConfirmRequest,
  ): Promise<IRiderCashHandover> {
    const response = await axiosInstance.put(`${this.baseURL}/${id}/hub-confirm`, payload);
    return this.transformNode(unwrapEnvelope<IRiderCashHandover>(response.data, 'Could not close cash handover'));
  }

  async cancel(id: number, payload: RiderCashHandoverCancelRequest): Promise<IRiderCashHandover> {
    const response = await axiosInstance.put(`${this.baseURL}/${id}/cancel`, payload);
    return this.transformNode(unwrapEnvelope<IRiderCashHandover>(response.data, 'Could not cancel cash handover'));
  }

  async moveToDispute(
    id: number,
    payload: RiderCashHandoverMoveToDisputeRequest,
  ): Promise<IRiderCashHandover> {
    const response = await axiosInstance.put(`${this.baseURL}/${id}/move-to-dispute`, payload);
    return this.transformNode(unwrapEnvelope<IRiderCashHandover>(response.data, 'Could not move cash handover to dispute'));
  }

  async resolveDispute(
    id: number,
    payload: RiderCashHandoverDisputeCloseRequest,
  ): Promise<IRiderCashHandover> {
    const response = await axiosInstance.put(`${this.baseURL}/${id}/resolve-dispute`, payload);
    return this.transformNode(unwrapEnvelope<IRiderCashHandover>(response.data, 'Could not close cash dispute'));
  }
}

const unwrapEnvelope = <T>(envelope: LyskraftEnvelope<T>, fallbackMessage: string): T => {
  if (envelope?.success === false || envelope?.status?.type === 'ERROR') {
    const error = new Error(envelope?.status?.message || envelope?.status?.key || fallbackMessage) as Error & {
      statusCode?: number;
      statusKey?: string;
    };
    error.statusCode = envelope?.status?.code;
    error.statusKey = envelope?.status?.key;
    throw error;
  }
  return envelope?.data as T;
};

const stripOtpFromHandover = (handover: IRiderCashHandover): IRiderCashHandover => ({
  ...handover,
  otp: undefined,
});

const toSearchQuery = (request?: RiderCashHandoverSearchRequest): SearchQuery => {
  const filters: Array<SearchFilter> = [];
  if (request?.riderId != null) {
    filters.push({ filterType: 'EQ', field: 'riderId', value: String(request.riderId) });
  }
  if (request?.facilityId != null) {
    filters.push({ filterType: 'EQ', field: 'facilityId', value: String(request.facilityId) });
  }
  if (request?.sourceFacilityId != null) {
    filters.push({ filterType: 'EQ', field: 'sourceFacilityId', value: String(request.sourceFacilityId) });
  }
  if (request?.receivingFacilityId != null) {
    filters.push({ filterType: 'EQ', field: 'receivingFacilityId', value: String(request.receivingFacilityId) });
  }
  if (request?.packageId != null) {
    filters.push({ filterType: 'EQ', field: 'packageId', value: String(request.packageId) });
  }
  if (request?.collectionPartyType) {
    filters.push({ filterType: 'EQ', field: 'collectionPartyType', value: request.collectionPartyType });
  }
  if (request?.salesChannelOrderId?.trim()) {
    filters.push({ filterType: 'EQ', field: 'salesChannelOrderId', value: request.salesChannelOrderId.trim() });
  }
  if (request?.customerName?.trim()) {
    filters.push({ filterType: 'EQ', field: 'customerName', value: request.customerName.trim() });
  }
  if (request?.statuses?.length) {
    filters.push({ filterType: 'EQ', field: 'statuses', value: request.statuses.join(',') });
  }
  if (request?.deliveredFrom) {
    filters.push({ filterType: 'EQ', field: 'deliveredFrom', value: request.deliveredFrom });
  }
  if (request?.deliveredTo) {
    filters.push({ filterType: 'EQ', field: 'deliveredTo', value: request.deliveredTo });
  }
  if (request?.closedFrom) {
    filters.push({ filterType: 'EQ', field: 'closedFrom', value: request.closedFrom });
  }
  if (request?.closedTo) {
    filters.push({ filterType: 'EQ', field: 'closedTo', value: request.closedTo });
  }
  return { filters };
};

export default RiderCashHandoverApiClient;
